import React, { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Grid, makeStyles } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import SydButton from '../../../../../../commonDesign/Button'
import { ADMIN_ROUTES, ALERT_SEVERITY, ALERT_VARIANT, USER_TYPE } from '../../../../../../../constants'
import { useCreateUserBatchMutation } from '../../../../../../../api/users'
import SydReadOnly from '../../../../../../commonDesign/SydReadOnly'
import { useFormattingContext } from '../../../../../../organisms/FormattingProvider/FormattingContext'
import { postNamedQuery } from '../../../../../../../service'
import SydModal from '../../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import Alert from '../../../../../../atoms/Alert'
import { UserSelectTable } from './UserSelectTable'
import { useColumns } from './UserSelectTable/hooks'

const useStyles = makeStyles({
  container: {
    padding: '1rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 50px)',
    overflow: 'hidden',
    gap: '1rem'
  },
  formContainer: {
    padding: '0 1rem',
    display: 'flex',
    flexDirection: 'column',
    gridGap: '1rem',
    overflow: 'hidden'
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem'
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    padding: '0 1rem'
  },
  invitationTable: {
    borderCollapse: 'collapse',
    fontSize: '12px',
    width: '100%',
    '& thead tr th': {
      padding: '6px'
    },
    '& tbody td': {
      padding: '6px'
    }
  }
})

export const BulkInviteUsersForm = () => {
  const classes = useStyles()
  const history = useHistory()
  const { formatter } = useFormattingContext()
  const columns = useColumns({ formatter, classes })
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const form = useForm({
    defaultValues: {
      batchType: 'bulk-invite-users',
      externalIds: [],
      userConfirmed: false
    }
  })
  const { control, setValue, watch } = form
  const externalIds = watch('externalIds')

  const { createBatch, isLoading } = useCreateUserBatchMutation()
  const onSubmit = useCallback(async (values) => {
    if (!values.userConfirmed) {
      return setShowConfirmModal(true)
    }
    try {
      const userBatch = await createBatch({
        batchType: values.batchType,
        data: values.externalIds.map(externalUserId => ({
          externalUserId
        }))
      })

      history.push(`${ADMIN_ROUTES.USER_BATCHES}/batch/${userBatch.userBatchId}`)
    } catch (err) {
      form.setError('root', {
        type: 'custom',
        message: err?.message ?? 'An error occurred while creating the user batch. Please try again.'
      })
    }
  }, [createBatch, history, form])

  const onSelectUser = useCallback((_externalIds) => {
    setValue('externalIds', _externalIds)
  }, [setValue])

  const { data: invitableUsers, isLoading: invitableUsersIsLoading } = useQuery({
    queryKey: ['users', 'invitableUsers'],
    queryFn: async () => {
      const response = await postNamedQuery('users', 'list-invitable-users')
      return response.data?.users
    }
  })

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className={classes.container}>
        <div className={classes.formContainer}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4} className={classes.inputContainer}>
              <Controller
                name='batchType'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Batch Type' required>
                    <SydReadOnly size='sm'>Bulk Invite Users</SydReadOnly>
                  </SydLabel>
                )}
              />
            </Grid>
            <Grid item md={8}>
              <p>
                <strong>Instructions</strong>
              </p>
              <ul>
                <li>The list below shows all users who have not accepted an invitation</li>
                <li>Select any/all users you would like to invite and then click the start batch button</li>
              </ul>
              {form.formState.errors?.root?.message && (
                <Alert variant={ALERT_VARIANT.standard} severity={ALERT_SEVERITY.error}>{form.formState.errors.root.message}</Alert>
              )}
            </Grid>
            <Grid item sm={12}>
              <hr />
            </Grid>
          </Grid>

          <UserSelectTable
            onSelectChange={onSelectUser}
            hasSearch
            selectedCount={externalIds?.length}
            data={invitableUsers ?? []}
            isLoading={invitableUsersIsLoading}
            columns={columns}
          />
        </div>

        <div className={classes.actionsContainer}>
          <SydButton
            disabled={isLoading || !externalIds?.length}
            type='submit'
            variant='primary'
            processing={isLoading}
          >
            {isLoading ? 'Processing...' : !externalIds?.length ? 'Select Users...' : 'Start Batch'}
          </SydButton>
        </div>
      </div>

      <SydModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title='Confirm Batch'
        subtitle='Confirm that you would like the following users to get email invites'
      >
        <UserSelectTable
          data={externalIds.map(externalId => ({
            ...(invitableUsers?.find(user => user.externalUserId === externalId) ?? {}),
            externalId
          }))}
          selectedCount={externalIds?.length}
          isLoading={invitableUsersIsLoading}
          hasSearch={false}
          hasFilter={false}
          columns={[
            { Header: 'User Id', accessor: 'userId' },
            { Header: 'External id', accessor: 'externalId' },
            { Header: 'User Type', accessor: 'userTypeId', Cell: (params) => Number(params.value) === USER_TYPE.WEALTH_OWNER ? 'Wealth Owner' : 'Advisor' },
            { Header: 'First Name', accessor: 'firstName' },
            { Header: 'Last Name', accessor: 'lastName' },
            { Header: 'Email', accessor: 'email' }
          ]}
        />
        <br />
        <SydModalActions>
          <SydButton
            key='cancel'
            variant='secondary'
            onClick={() => setShowConfirmModal(false)}
          >
            Cancel
          </SydButton>,
          <SydButton
            key='confirm'
            variant='primary'
            onClick={() => {
              setValue('userConfirmed', true)
              setShowConfirmModal(false)
              form.handleSubmit(onSubmit)()
            }}
          >
            Confirm
          </SydButton>
        </SydModalActions>
      </SydModal>
    </form>
  )
}
